import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '../shared';
import ModalShell from '../shared/ModalShell';
import { useToastContext } from '../../contexts/ToastContext';

const EventEndModal = ({
  isOpen,
  onClose,
  onEnd,
  isLoading,
  eventPassed = false,
  willAutoStart = false,
}) => {
  const { showToast } = useToastContext();
  const [notes, setNotes] = useState('');
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setNotes('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setNotes('');
    onCloseRef.current();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = onEnd({
        notes: notes.trim() || null,
        screenshots: null,
      });

      if (result && typeof result.then === 'function') {
        await result;
      }
    } catch (error) {
      console.error('Error ending event:', error);
      showToast(
        error?.response?.data?.message || error?.message || 'Could not end event',
        'error'
      );
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy="event-end-title"
      panelClassName="max-w-2xl"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between z-10">
          <h2 id="event-end-title" className="text-xl font-bold text-gray-900 dark:text-gray-50">
            End Event
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 !rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div
            className={`rounded-xl border p-4 ${
              eventPassed
                ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/40'
                : 'border-sky-200 dark:border-sky-800/50 bg-sky-50 dark:bg-sky-950/40'
            }`}
          >
            <p
              className={`text-sm leading-relaxed ${
                eventPassed
                  ? 'text-amber-900 dark:text-amber-200'
                  : 'text-sky-900 dark:text-sky-200'
              }`}
            >
              {eventPassed ? (
                <>
                  <strong>Event date has passed.</strong> Ending now marks this booking as completed
                  {willAutoStart ? ' and records start + end for your timeline' : ''}.
                </>
              ) : (
                <>
                  <strong>Note:</strong> Ending the event updates the booking status to
                  &quot;Completed&quot;.
                </>
              )}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
              placeholder="Add any notes about ending the event..."
              maxLength={2000}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {notes.length}/2000 characters
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="!rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
              className="!rounded-xl"
            >
              End Event
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
};

export default EventEndModal;
